import {z} from 'zod';
import dedent from 'dedent';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'set-code-component-states' as const;

const stateZod = z.object({
  id: z
    .string()
    .describe(
      'Stable, kebab-case id for the state (used as a wire value across the Designer). Keep it stable across edits.'
    ),
  label: z
    .string()
    .describe(
      'Human-readable label describing the state from the end-user perspective, e.g. "Step 2: Profile", "Empty cart", "Submitted".'
    ),
  props: z
    .record(z.unknown())
    .optional()
    .describe(
      'Serializable props that seed the component into this state (merged on top of the default props). For example { initialStep: 2 } or { submitted: true }. Never include props.Variant values.'
    ),
});

const argumentZod = z.object({
  componentId: z
    .string()
    .describe('The ID of the code component to set preview states for.'),
  states: z
    .array(stateZod)
    .describe(
      'The full set of meaningful states the component can be in. Pass an empty array to clear the stored states.'
    ),
});

export const setCodeComponentStatesTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Set code component states',
  prettyErrors: {
    fallback: 'Unable to set code component states.',
    patterns: [
      {
        match: 'not found',
        message: 'Component not found.',
      },
    ],
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Records the set of states a stateful code component can be in (multi-step forms, tabs, accordions, modals, wizards, …).

      Call this after generating or editing a component that uses 'useState' AND has meaningfully distinct states to preview, listing each state as { id, label, props }, where 'props' seeds the component into that state (the same initial-state props the component reads in its 'useState' initializers). Never include props.Variant values in a state's props.

      Record a state only when the set of rendered elements differs — sections appear or disappear, the layout changes, or different actions become available. Text and values changing inside an unchanged structure are content, not state. So: map overview and selected-location detail, gallery and open lightbox, each step of a multi-step form plus success, accordion or navigation closed and open, empty vs populated list. But NOT filters, search, sort, pagination, slides, or values — including a price or total that updates live. A calculator with an explicit Calculate button is empty + result; one whose total updates live has no states. Selection counts only when it reveals a distinct detail interface, not when it merely highlights.

      When uncertain, do not record a state — under-recording beats filling the canvas with states that are not states. For components whose interactive controls only re-value or re-order an otherwise-identical layout, leave the states empty (do not call this tool) until the user explicitly asks for previews.

      The states are stored Designer-side on the component (source.meta.states) — they are NOT written into the component's source file and are NOT shipped in the published bundle. The Designer uses them to render the multi-state preview grid and the Navigator States section. Pass an empty 'states' array to clear them.
    `,
    inputSchema: argumentZod,
  },
  type: 'designer',
  permission: [{resource: 'SiteSymbol', action: 'modify'}],
  confirmBeforeExecuting: false,
  timeout: 30000,
  // Gates the tool out of the assistant's toolset when the multi-state
  // visual-editing feature is off (see `filterToolsByFeatureGate`).
  featureGate: FEATURE_FLAGS.AICC_VISUAL_EDITING_STATES,
});
